import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Brain, Camera, Mic, Sparkles, ShieldCheck } from "lucide-react";

const HERO_IMG = "/img/hero-plant.jpg";
const ABSTRACT_IMG = "/img/ai-abstract.jpg";

const features = [
  {
    icon: Camera,
    title: "Computer Vision",
    body: "From defect detection to PPE compliance — articles that translate CV breakthroughs into shop-floor wins.",
  },
  {
    icon: Brain,
    title: "Large Language Models",
    body: "Distill agentic AI, RAG, and reasoning advances into stories plant managers actually want to read.",
  },
  {
    icon: Mic,
    title: "Voice Bots",
    body: "Cover STT/TTS, voice agents in manufacturing, and the rise of conversational operations.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      {/* Nav */}
      <header
        data-testid="landing-header"
        className="sticky top-0 z-40 bg-ink/80 backdrop-blur-xl border-b border-hairline"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-3"
          >
            <div className="w-8 h-8 bg-safety flex items-center justify-center">
              <span className="font-display font-black text-white text-sm">L</span>
            </div>
            <div>
              <div className="font-display text-base font-bold tracking-tightest leading-none">
                Lingent
              </div>
              <div className="label-mono text-[10px] text-muted-foreground leading-none mt-1">
                by WiseWit Solutions
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 label-mono text-bone/70">
            <a href="#capabilities" className="hover:text-bone">Capabilities</a>
            <a href="#how-it-works" className="hover:text-bone">How it works</a>
            <a href="#mission" className="hover:text-bone">Mission</a>
          </nav>
          <Link to="/login" data-testid="nav-admin-login">
            <Button
              variant="outline"
              className="rounded-none border-hairline bg-transparent text-bone hover:bg-bone hover:text-ink label-mono"
            >
              Admin Console
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-hairline">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-ink/82" />
        <div className="absolute inset-0 -z-10 grid-bg opacity-30" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-36 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 px-3 py-1.5 border border-hairline bg-surface/60 backdrop-blur label-mono text-bone/80">
              <span className="w-1.5 h-1.5 bg-safety animate-pulse" />
              Lingent · LinkedIn AI Agent · v0.1
            </div>
            <h1
              data-testid="hero-title"
              className="mt-8 text-5xl md:text-7xl lg:text-8xl font-display font-black tracking-tightest leading-[0.95] text-balance"
            >
              Daily AI stories.
              <br />
              <span className="text-safety">Zero blank pages.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-bone/70 leading-relaxed">
              Lingent is the in-house ghostwriter for WiseWit Solutions — turning raw
              ideas on Computer Vision, LLMs and VoiceBots into LinkedIn articles ready
              to publish in under a minute.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/login" data-testid="hero-cta-launch">
                <Button
                  size="lg"
                  className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono px-8 h-12"
                >
                  Launch Lingent <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a
                href="https://www.linkedin.com/company/wisewit-solutions/"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-linkedin"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono px-8 h-12"
                >
                  Visit WiseWit on LinkedIn
                </Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex flex-col gap-4 self-end">
            <Stat k="01" v="Article / day" />
            <Stat k="03" v="Verticals covered" />
            <Stat k="<60s" v="Draft to ready" />
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <div className="label-mono text-safety">// 01 — capabilities</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-3">
                Built for the operations frontier.
              </h2>
            </div>
            <p className="hidden md:block max-w-sm text-bone/60">
              Three industry lenses, one editorial brain. Lingent writes for the people who
              run plants, not algorithm Twitter.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-hairline border border-hairline">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-ink p-8 hover:bg-surface transition-colors"
                data-testid={`feature-${f.title.replace(/\s/g, "-").toLowerCase()}`}
              >
                <f.icon className="w-7 h-7 text-safety" strokeWidth={1.5} />
                <h3 className="font-display text-2xl font-bold mt-6">{f.title}</h3>
                <p className="text-bone/65 mt-3 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-mono text-safety">// 02 — workflow</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-3">
              Idea → Article → Approval → Publish.
            </h2>
            <p className="mt-6 text-bone/65 leading-relaxed">
              You drag the orange Lingent icon anywhere on the dashboard. Type a topic.
              Drop a few notes. Claude Sonnet 4.5 drafts a LinkedIn-ready article in
              seconds. Approve it. Copy. Post. Done.
            </p>
            <ul className="mt-8 space-y-4 label-mono text-bone/80">
              <li className="flex items-start gap-4">
                <span className="text-safety">01</span>
                <span>Open the floating Lingent panel as admin.</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-safety">02</span>
                <span>Enter topic + key points; pick category & tone.</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-safety">03</span>
                <span>Generate article + optional cover image.</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-safety">04</span>
                <span>Approve, copy to clipboard, open LinkedIn, paste.</span>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-hairline bg-surface p-6 md:p-10 relative overflow-hidden">
              <img src={ABSTRACT_IMG} alt="" className="rounded-none w-full max-h-[480px] object-cover" />
              <div className="absolute top-6 left-6 px-3 py-1.5 bg-ink border border-hairline label-mono">
                LINGENT · ACTIVE
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section id="mission" className="border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="label-mono text-safety">// 03 — mission</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-3">
              Keep the page alive.<br />Catch industries automating with AI.
            </h2>
            <p className="mt-6 text-bone/65 max-w-2xl leading-relaxed">
              WiseWit Solutions helps industrial plants deploy Computer Vision, LLMs and
              VoiceBots. Lingent is how we show up in our customers' feeds — every day,
              with substance.
            </p>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-4">
            <KPI k="Cadence" v="Daily" />
            <KPI k="Approval" v="Human-in-loop" />
            <KPI k="Model" v="Sonnet 4.5" />
            <KPI k="Image" v="Nano Banana" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h3 className="font-display text-3xl md:text-4xl font-black tracking-tightest max-w-xl">
            Ready to write tomorrow's article tonight?
          </h3>
          <Link to="/login" data-testid="cta-bottom-launch">
            <Button
              size="lg"
              className="rounded-none bg-safety hover:bg-safety/90 text-white label-mono px-10 h-14"
            >
              Enter Admin Console <Sparkles className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-10 px-6 lg:px-10 max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-bone/50 label-mono">
        <div className="flex items-center gap-3">
          <img src="/wisewit-logo-mark.png" alt="WiseWit Solutions" className="h-8 w-auto opacity-80" />
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-4 h-4" />
            <span>© 2026 WiseWit Solutions · Lingent v0.3</span>
          </div>
        </div>
        <a
          href="https://www.linkedin.com/company/wisewit-solutions/"
          target="_blank"
          rel="noreferrer"
          className="hover:text-bone"
        >
          linkedin.com/company/wisewit-solutions
        </a>
      </footer>
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div className="bg-surface/80 backdrop-blur border border-hairline p-6">
    <div className="label-mono text-safety">{k}</div>
    <div className="font-display text-3xl font-bold mt-2">{v}</div>
  </div>
);

const KPI = ({ k, v }) => (
  <div className="border border-hairline p-5 bg-surface">
    <div className="label-mono text-bone/50">{k}</div>
    <div className="font-display text-2xl font-bold mt-2">{v}</div>
  </div>
);
